import { TENANT_SLUG_QUARANTINE_DAYS } from "@repo/shared";
import type { TenantWithOwner } from "@repo/shared/types";
import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError, patch, post } from "@/lib/api/_client";
import type { PartnerInfo } from "@/lib/hooks/use-admin";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./tenant-dialog.i18n";

type TenantTier = "basic" | "business" | "enterprise";

type TenantDialogProps = {
  open: boolean;
  tenant: TenantWithOwner | null;
  onClose: () => void;
  mutate?: () => void;
  isQuantfolio?: boolean;
  partners?: PartnerInfo[];
};

const TIER_OPTIONS: TenantTier[] = ["basic", "business", "enterprise"];

export function TenantDialog({
  open,
  tenant,
  onClose,
  mutate,
  isQuantfolio = false,
  partners = [],
}: TenantDialogProps) {
  const t = useT(T);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    tier: "enterprise" as TenantTier,
    ownerTenantId: null as string | null,
  });
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name,
        slug: tenant.slug,
        tier: (tenant.tier as TenantTier) || "enterprise",
        ownerTenantId: tenant.ownerTenantId || null,
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        tier: "enterprise",
        ownerTenantId: null,
      });
    }
    setError(null);
  }, [tenant, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    startTransition(async () => {
      try {
        const isEdit = !!tenant;
        if (isEdit) {
          await patch(`/internal/admin/tenants/${tenant.id}`, formData);
        } else {
          await post("/internal/admin/tenants", formData);
        }

        mutate?.();
        onClose();
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || t.somethingWentWrong);
        } else {
          setError(t.failedToSaveTenant);
        }
      }
    });
  };

  const handleSlugChange = (value: string) => {
    // Auto-format slug
    const slug = value.toLowerCase().replace(/[^a-z0-9-]/g, "-");
    setFormData({ ...formData, slug });
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogScrollContent className="max-w-2xl">
        <DialogScrollHeader>
          <DialogTitle>{tenant ? t.editTenant : t.addNewTenant}</DialogTitle>
          <DialogDescription>{tenant ? t.updateTenantInfo : t.configureNewOrg}</DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {t.organizationName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.orgNamePlaceholder}
                required
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">
                {t.slug} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder={t.slugPlaceholder}
                required
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">{t.slugHelp}</p>
              {tenant && formData.slug !== tenant.slug && (
                <div className="rounded-md border bg-muted/50 p-3 text-sm space-y-1">
                  <p className="font-medium">{t.slugChangeTitle}</p>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                    <li>
                      {t.slugChangeRedirect(tenant.slug, String(TENANT_SLUG_QUARANTINE_DAYS))}
                    </li>
                    <li>{t.slugChangeSso(String(TENANT_SLUG_QUARANTINE_DAYS))}</li>
                    <li>{t.slugChangeSessions}</li>
                    <li>{t.slugChangeIntegrations}</li>
                  </ul>
                </div>
              )}
            </div>

            {isQuantfolio && (
              <div className="space-y-2">
                <Label htmlFor="tier">{t.subscriptionTier}</Label>
                <Select
                  value={formData.tier}
                  onValueChange={(value) => setFormData({ ...formData, tier: value as TenantTier })}
                  disabled={isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((tier) => (
                      <SelectItem key={tier} value={tier}>
                        {tier === "basic"
                          ? t.tierBasic
                          : tier === "business"
                            ? t.tierBusiness
                            : t.tierEnterprise}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.tierHelp}</p>
              </div>
            )}

            {isQuantfolio && tenant && partners.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="ownerTenantId">{t.ownerPartner}</Label>
                <Select
                  value={formData.ownerTenantId || "_none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, ownerTenantId: value === "_none" ? null : value })
                  }
                  disabled={isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.noOwnerQuantfolio} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">{t.noOwnerQuantfolio}</SelectItem>
                    {partners.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.assignToPartner}</p>
              </div>
            )}
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {tenant ? t.save : t.addTenant}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
